// Tracks long-running foreground work (blob uploads, SharePoint transfers,
// failed-file reprocessing) so the inactivity timer can hold a session open
// while a transfer is genuinely in flight, without weakening the idle timeout
// for a user who has simply walked away from the desk.
//
// Registrations are opaque tokens rather than a counter, so an end() called
// twice (retry paths, React re-mounts) cannot release a task it does not own.
//
// Every token also carries a hard expiry. A registration that leaks - an upload
// promise that never settles, an unmount that skips its cleanup - must never be
// able to disable the 30 minute idle timeout for the rest of the session, which
// is the control Infosec is measuring. The expiry is a leak guard, not the
// policy limit: it is deliberately longer than any realistic transfer so it only
// fires on a bug, and it logs when it does.

import logger from "../utils/logger";

export const MAX_LONG_RUNNING_TASK_MS = 4 * 60 * 60 * 1000;

const activeTasks = new Map();
const listeners = new Set();

const notifyListeners = () => {
  const isActive = activeTasks.size > 0;
  listeners.forEach((listener) => {
    try {
      listener(isActive);
    } catch (error) {
      // A broken subscriber must not strand the task registry.
      logger.error("Activity monitor listener failed", error);
    }
  });
};

const releaseTask = (token) => {
  const task = activeTasks.get(token);
  if (!task) return;
  clearTimeout(task.expiryTimer);
  activeTasks.delete(token);
  if (activeTasks.size === 0) {
    notifyListeners();
  }
};

/**
 * Marks a long-running operation as started.
 *
 * @returns {() => void} an idempotent function that marks it finished. Always
 *   call it from a `finally` block or an effect cleanup.
 */
export const beginLongRunningTask = (
  label = "long-running-task",
  maxDurationMs = MAX_LONG_RUNNING_TASK_MS
) => {
  const token = Symbol(label);

  const expiryTimer = setTimeout(() => {
    logger.warn(
      `Long-running task "${label}" exceeded its maximum duration and was released so the idle timeout can resume.`
    );
    releaseTask(token);
  }, maxDurationMs);

  activeTasks.set(token, { label, expiryTimer });
  if (activeTasks.size === 1) {
    notifyListeners();
  }

  return () => releaseTask(token);
};

export const isLongRunningTaskActive = () => activeTasks.size > 0;

export const getActiveLongRunningTaskLabels = () =>
  Array.from(activeTasks.values(), (task) => task.label);

/**
 * Subscribes to transitions between "nothing running" and "something running".
 * The listener is not called for every registration, only when the overall state
 * flips, because that is the only thing the inactivity timer needs to react to.
 */
export const subscribeLongRunningTasks = (listener) => {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetActivityMonitor = () => {
  activeTasks.forEach((task) => clearTimeout(task.expiryTimer));
  activeTasks.clear();
  listeners.clear();
};
